import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@clerk/clerk-react";
import { supabase } from "@/integrations/supabase/client";

async function invokeFPL(
  fnName: string,
  body?: Record<string, unknown>,
  token?: string | null,
) {
  const { data, error } = await supabase.functions.invoke(fnName, {
    body,
    ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
  });
  if (error) throw error;
  return data;
}

export function useFPLBootstrap() {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: ["fpl-bootstrap"],
    queryFn: async () => {
      const token = await getToken().catch(() => null);
      return invokeFPL("fpl-bootstrap", undefined, token);
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    retry: 1,
  });
}

export function useFPLGameweekLive(gameweekId: number | null) {
  const { getToken, isSignedIn } = useAuth();
  return useQuery({
    queryKey: ["fpl-gameweek-live", gameweekId],
    queryFn: async () => {
      const token = await getToken();
      return invokeFPL("fpl-gameweek-live", { gameweek_id: gameweekId }, token);
    },
    enabled: !!gameweekId && !!isSignedIn,
    staleTime: 3 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    retry: false,
  });
}

export function useFPLPlayerDetail(playerId: number | null) {
  const { getToken, isSignedIn } = useAuth();
  return useQuery({
    queryKey: ["fpl-player-detail", playerId],
    queryFn: async () => {
      const token = await getToken();
      return invokeFPL("fpl-player-detail", { player_id: playerId }, token);
    },
    enabled: !!playerId && !!isSignedIn,
    staleTime: 30 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    retry: false,
  });
}

export function useFPLDreamTeam(gameweekId: number | null) {
  const { getToken, isSignedIn } = useAuth();
  return useQuery({
    queryKey: ["fpl-dream-team", gameweekId],
    queryFn: async () => {
      const token = await getToken();
      return invokeFPL("fpl-dream-team", { gameweek_id: gameweekId }, token);
    },
    enabled: !!gameweekId && !!isSignedIn,
    staleTime: 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    retry: false,
  });
}

export function useManagerPerformance(opts?: { fplTeamId?: number; leagueId?: number; force?: boolean }) {
  const { getToken, isSignedIn } = useAuth();
  return useQuery({
    queryKey: ["fpl-manager-performance", opts?.fplTeamId ?? "self", opts?.leagueId ?? null],
    queryFn: async () => {
      const token = await getToken();
      return invokeFPL("fpl-manager-performance", {
        fpl_team_id: opts?.fplTeamId,
        league_id: opts?.leagueId,
        force: opts?.force,
      }, token);
    },
    enabled: !!isSignedIn,
    staleTime: 10 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    retry: false,
  });
}
export function useFPLManagerLookup(managerId: number | null, enabled: boolean) {
  const { getToken, isSignedIn } = useAuth();
  return useQuery({
    queryKey: ["fpl-manager-lookup", managerId],
    queryFn: async () => {
      const token = await getToken();
      return invokeFPL("fpl-manager-lookup", { manager_id: managerId }, token);
    },
    enabled: !!managerId && enabled && !!isSignedIn,
    staleTime: 5 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    retry: false,
  });
}

export function useFPLSuggestions() {
  const { getToken, isSignedIn } = useAuth();
  return useQuery({
    queryKey: ["fpl-suggestions"],
    queryFn: async () => {
      const token = await getToken();
      return invokeFPL("fpl-suggestions", undefined, token);
    },
    enabled: !!isSignedIn,
    staleTime: 15 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    retry: false,
  });
}



// Helper to get player photo URL
export function getPlayerPhotoUrl(photo: string): string {
  if (!photo) return "";
  const code = photo.replace(".jpg", "");
  return `https://resources.premierleague.com/premierleague/photos/players/110x140/p${code}.png`;
}

// Helper to get team badge URL
export function getTeamBadgeUrl(teamCode: number): string {
  return `https://resources.premierleague.com/premierleague/badges/70/t${teamCode}.png`;
}

// Position labels
export const POSITION_LABELS: Record<number, string> = {
  1: "GK", 2: "DEF", 3: "MID", 4: "FWD",
};

// FDR color mapping
export function getFDRColor(fdr: number): string {
  if (fdr <= 2) return "bg-green-500/20 text-green-400 border-green-500/30";
  if (fdr === 3) return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
  return "bg-red-500/20 text-red-400 border-red-500/30";
}

// Status display
export function getStatusDisplay(status: string): { emoji: string; label: string; color: string } {
  switch (status) {
    case "a": return { emoji: "✅", label: "Available", color: "text-green-400" };
    case "d": return { emoji: "🟡", label: "Doubtful", color: "text-yellow-400" };
    case "i": return { emoji: "🔴", label: "Injured", color: "text-red-400" };
    case "u": return { emoji: "🔴", label: "Unavailable", color: "text-red-400" };
    default: return { emoji: "❓", label: "Unknown", color: "text-muted-foreground" };
  }
}
